import * as LightweightCharts from 'lightweight-charts'
import React, { useEffect, useRef } from 'react'
import { useChartTokens } from './useChartTokens'

export interface Candle {
  time: number
  open: number
  high: number
  low: number
  close: number
}

interface CandleChartProps {
  candles: Candle[]
  entry?: number
  liquidation?: number
  height?: number
}

const CandleChart: React.FC<CandleChartProps> = ({ candles, entry, liquidation, height = 300 }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<LightweightCharts.IChartApi | null>(null)
  const seriesRef = useRef<any>(null)
  const entryLineRef = useRef<LightweightCharts.IPriceLine | null>(null)
  const liquidationLineRef = useRef<LightweightCharts.IPriceLine | null>(null)
  const colors = useChartTokens(chartContainerRef)

  useEffect(() => {
    if (!chartContainerRef.current) return

    const chart = LightweightCharts.createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: height,
      layout: {
        background: { type: LightweightCharts.ColorType.Solid, color: 'transparent' },
      },
      rightPriceScale: {
        borderVisible: false,
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        mode: LightweightCharts.CrosshairMode.Normal,
      },
    })

    const series = chart.addSeries(LightweightCharts.CandlestickSeries, {})

    chartRef.current = chart
    seriesRef.current = series

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: height,
        })
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
      if (chartRef.current) {
        chartRef.current.remove()
      }
      // The series (and its price lines) die with the chart; the effects below redraw on rebuild.
      entryLineRef.current = null
      liquidationLineRef.current = null
    }
  }, [height])

  // Colours come from the Plug tokens and follow the tone.
  useEffect(() => {
    if (!chartRef.current || !seriesRef.current) return

    chartRef.current.applyOptions({
      layout: {
        background: { type: LightweightCharts.ColorType.Solid, color: 'transparent' },
        textColor: colors.text3,
        fontFamily: colors.fontFamily || undefined,
      },
      grid: {
        vertLines: { visible: true, color: colors.border },
        horzLines: { visible: true, color: colors.border },
      },
      timeScale: { borderColor: colors.border },
    })

    seriesRef.current.applyOptions({
      upColor: colors.success,
      downColor: colors.danger,
      borderUpColor: colors.success,
      borderDownColor: colors.danger,
      wickUpColor: colors.success,
      wickDownColor: colors.danger,
    })
  }, [colors, height])

  useEffect(() => {
    if (!seriesRef.current || !candles?.length) return

    const formattedCandles: LightweightCharts.CandlestickData[] = candles.map((candle) => ({
      time: candle.time as any,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
    }))

    seriesRef.current.setData(formattedCandles)

    if (chartRef.current) {
      chartRef.current.timeScale().fitContent()
      chartRef.current.timeScale().scrollToRealTime()
    }
  }, [candles, height])

  useEffect(() => {
    if (!seriesRef.current) return

    if (entryLineRef.current) {
      seriesRef.current.removePriceLine(entryLineRef.current)
      entryLineRef.current = null
    }
    if (liquidationLineRef.current) {
      seriesRef.current.removePriceLine(liquidationLineRef.current)
      liquidationLineRef.current = null
    }

    if (!!liquidation) {
      liquidationLineRef.current = seriesRef.current.createPriceLine({
        price: liquidation,
        color: colors.danger,
        lineStyle: 2, // dashed
        axisLabelVisible: true,
        title: 'Liquidation',
      })
    }

    if (!!entry) {
      entryLineRef.current = seriesRef.current.createPriceLine({
        price: entry,
        color: colors.success,
        lineStyle: 2, // dashed
        axisLabelVisible: true,
        title: 'Entry',
      })
    }
  }, [entry, liquidation, colors.success, colors.danger, height])

  return (
    <div
      ref={chartContainerRef}
      className="w-full overflow-hidden rounded-lg bg-transparent"
      style={{ height }}
    />
  )
}

export default CandleChart
